import React, { useState, useEffect, useRef, useCallback } from 'react';
import useKeepAwake from '../hooks/useKeepAwake';
import KeepAwakeToggle from './KeepAwakeToggle';
import { ArrowLeft, X, Clock, ChevronRight, ChevronLeft, Trash2, Upload, Lock, Unlock, Images, SlidersHorizontal, Maximize2 } from 'lucide-react';
import { BODY_ZONES } from '../data/stretches';
import usePoseMedia from '../hooks/usePoseMedia';
import PoseMediaControls from './PoseMediaControls';
import UploadProgressOverlay from './UploadProgressOverlay';
import SessionAudioPanel from './SessionAudioPanel';

const ZONE_COLORS = {
  cou: '#f59e0b', epaules: '#3b82f6', dos: '#8b5cf6', bras: '#ec4899',
  poitrine: '#ef4444', hanches: '#10b981', cuisses: '#f97316', mollets: '#06b6d4'
};

const HIDDEN_KEY = 'stretch_hidden_images';
const LOCKED_KEY = 'stretch_locked_images';

const getHidden = () => { try { return JSON.parse(localStorage.getItem(HIDDEN_KEY) || '{}'); } catch { return {}; } };
const getLocked = () => { try { return JSON.parse(localStorage.getItem(LOCKED_KEY) || '{}'); } catch { return {}; } };
const saveHidden = (d) => localStorage.setItem(HIDDEN_KEY, JSON.stringify(d));
const saveLocked = (d) => localStorage.setItem(LOCKED_KEY, JSON.stringify(d));

const BodySVG = ({ onZoneClick, activeZone }) => (
  <svg viewBox="0 0 200 440" className="h-full w-auto mx-auto" style={{ maxHeight: '100%', maxWidth: 'min(220px, 100%)' }}>
    {/* Head */}
    <ellipse cx="100" cy="35" rx="22" ry="28" fill="#1e293b" stroke="#334155" strokeWidth="1" />
    {/* Neck */}
    <rect x="90" y="58" width="20" height="18" rx="4"
      fill={activeZone === 'cou' ? ZONE_COLORS.cou : '#1e293b'}
      stroke={ZONE_COLORS.cou} strokeWidth="1.5" opacity={activeZone === 'cou' ? 1 : 0.6}
      onClick={() => onZoneClick('cou')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-cou" />
    {/* Shoulders */}
    <path d="M70 76 Q60 76 50 90 L65 90 Z" fill={activeZone === 'epaules' ? ZONE_COLORS.epaules : '#1e293b'}
      stroke={ZONE_COLORS.epaules} strokeWidth="1.5" opacity={activeZone === 'epaules' ? 1 : 0.6}
      onClick={() => onZoneClick('epaules')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-epaules-l" />
    <path d="M130 76 Q140 76 150 90 L135 90 Z" fill={activeZone === 'epaules' ? ZONE_COLORS.epaules : '#1e293b'}
      stroke={ZONE_COLORS.epaules} strokeWidth="1.5" opacity={activeZone === 'epaules' ? 1 : 0.6}
      onClick={() => onZoneClick('epaules')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-epaules-r" />
    {/* Chest/Abs */}
    <rect x="65" y="90" width="70" height="55" rx="8"
      fill={activeZone === 'poitrine' ? ZONE_COLORS.poitrine : '#1e293b'}
      stroke={ZONE_COLORS.poitrine} strokeWidth="1.5" opacity={activeZone === 'poitrine' ? 1 : 0.6}
      onClick={() => onZoneClick('poitrine')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-poitrine" />
    {/* Back overlay (behind chest - shown via label) */}
    <rect x="72" y="95" width="56" height="44" rx="4"
      fill={activeZone === 'dos' ? ZONE_COLORS.dos : 'transparent'}
      stroke={activeZone === 'dos' ? ZONE_COLORS.dos : 'transparent'} strokeWidth="1.5"
      opacity={activeZone === 'dos' ? 0.4 : 0}
      onClick={() => onZoneClick('dos')} className="cursor-pointer" />
    {/* Arms */}
    <rect x="38" y="90" width="18" height="70" rx="8"
      fill={activeZone === 'bras' ? ZONE_COLORS.bras : '#1e293b'}
      stroke={ZONE_COLORS.bras} strokeWidth="1.5" opacity={activeZone === 'bras' ? 1 : 0.6}
      onClick={() => onZoneClick('bras')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-bras-l" />
    <rect x="144" y="90" width="18" height="70" rx="8"
      fill={activeZone === 'bras' ? ZONE_COLORS.bras : '#1e293b'}
      stroke={ZONE_COLORS.bras} strokeWidth="1.5" opacity={activeZone === 'bras' ? 1 : 0.6}
      onClick={() => onZoneClick('bras')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-bras-r" />
    {/* Hips */}
    <rect x="65" y="145" width="70" height="40" rx="8"
      fill={activeZone === 'hanches' ? ZONE_COLORS.hanches : '#1e293b'}
      stroke={ZONE_COLORS.hanches} strokeWidth="1.5" opacity={activeZone === 'hanches' ? 1 : 0.6}
      onClick={() => onZoneClick('hanches')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-hanches" />
    {/* Thighs */}
    <rect x="65" y="188" width="30" height="95" rx="10"
      fill={activeZone === 'cuisses' ? ZONE_COLORS.cuisses : '#1e293b'}
      stroke={ZONE_COLORS.cuisses} strokeWidth="1.5" opacity={activeZone === 'cuisses' ? 1 : 0.6}
      onClick={() => onZoneClick('cuisses')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-cuisses-l" />
    <rect x="105" y="188" width="30" height="95" rx="10"
      fill={activeZone === 'cuisses' ? ZONE_COLORS.cuisses : '#1e293b'}
      stroke={ZONE_COLORS.cuisses} strokeWidth="1.5" opacity={activeZone === 'cuisses' ? 1 : 0.6}
      onClick={() => onZoneClick('cuisses')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-cuisses-r" />
    {/* Calves */}
    <rect x="67" y="290" width="26" height="80" rx="10"
      fill={activeZone === 'mollets' ? ZONE_COLORS.mollets : '#1e293b'}
      stroke={ZONE_COLORS.mollets} strokeWidth="1.5" opacity={activeZone === 'mollets' ? 1 : 0.6}
      onClick={() => onZoneClick('mollets')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-mollets-l" />
    <rect x="107" y="290" width="26" height="80" rx="10"
      fill={activeZone === 'mollets' ? ZONE_COLORS.mollets : '#1e293b'}
      stroke={ZONE_COLORS.mollets} strokeWidth="1.5" opacity={activeZone === 'mollets' ? 1 : 0.6}
      onClick={() => onZoneClick('mollets')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-mollets-r" />
    {/* Feet */}
    <ellipse cx="80" cy="378" rx="16" ry="8" fill="#1e293b" stroke="#334155" strokeWidth="1" />
    <ellipse cx="120" cy="378" rx="16" ry="8" fill="#1e293b" stroke="#334155" strokeWidth="1" />
    {/* Labels */}
    <text x="100" y="38" textAnchor="middle" fill="#64748b" fontSize="8">Tête</text>
    <text x="100" y="70" textAnchor="middle" fill={ZONE_COLORS.cou} fontSize="7" fontWeight="600">Cou</text>
    <text x="44" y="84" textAnchor="middle" fill={ZONE_COLORS.epaules} fontSize="6" fontWeight="600">Épaule</text>
    <text x="156" y="84" textAnchor="middle" fill={ZONE_COLORS.epaules} fontSize="6" fontWeight="600">Épaule</text>
    <text x="100" y="120" textAnchor="middle" fill={ZONE_COLORS.poitrine} fontSize="7" fontWeight="600">Poitrine</text>
    <text x="26" y="130" textAnchor="middle" fill={ZONE_COLORS.bras} fontSize="6" fontWeight="600">Bras</text>
    <text x="174" y="130" textAnchor="middle" fill={ZONE_COLORS.bras} fontSize="6" fontWeight="600">Bras</text>
    <text x="100" y="170" textAnchor="middle" fill={ZONE_COLORS.hanches} fontSize="7" fontWeight="600">Hanches</text>
    <text x="80" y="240" textAnchor="middle" fill={ZONE_COLORS.cuisses} fontSize="7" fontWeight="600">Cuisse</text>
    <text x="120" y="240" textAnchor="middle" fill={ZONE_COLORS.cuisses} fontSize="7" fontWeight="600">Cuisse</text>
    <text x="80" y="335" textAnchor="middle" fill={ZONE_COLORS.mollets} fontSize="7" fontWeight="600">Mollet</text>
    <text x="120" y="335" textAnchor="middle" fill={ZONE_COLORS.mollets} fontSize="7" fontWeight="600">Mollet</text>
    {/* Dos button outside body */}
    <rect x="155" y="105" width="40" height="22" rx="6"
      fill={activeZone === 'dos' ? ZONE_COLORS.dos : '#1e293b'}
      stroke={ZONE_COLORS.dos} strokeWidth="1.5" opacity={activeZone === 'dos' ? 1 : 0.7}
      onClick={() => onZoneClick('dos')} className="cursor-pointer hover:opacity-100 transition-opacity"
      data-testid="zone-dos" />
    <text x="175" y="120" textAnchor="middle" fill={ZONE_COLORS.dos} fontSize="8" fontWeight="600"
      onClick={() => onZoneClick('dos')} className="cursor-pointer">Dos</text>
    <line x1="135" y1="116" x2="155" y2="116" stroke={ZONE_COLORS.dos} strokeWidth="1" strokeDasharray="3,2" />
  </svg>
);

const StretchingPanel = ({ isOpen, onClose, isAdmin = false }) => {
  const [selectedZone, setSelectedZone] = useState(null);
  const [expandedStretch, setExpandedStretch] = useState(null);
  const [fsVideo, setFsVideo] = useState(null);
  const [hidden, setHidden] = useState(getHidden);
  const [locked, setLocked] = useState(getLocked);
  const [imgIndex, setImgIndex] = useState({});
  const [showSessionAudio, setShowSessionAudio] = useState(false);
  const replaceRef = useRef(null);
  const [replaceTarget, setReplaceTarget] = useState(null);
  const { media: poseMedia, uploads: poseUploads, upload: uploadMedia, remove: removeMedia, toggleLock: toggleMediaLock } = usePoseMedia('stretch');

  useEffect(() => {
    if (isOpen) { setSelectedZone(null); setExpandedStretch(null); setImgIndex({}); }
  }, [isOpen]);

  const getVisibleImages = useCallback((stretch) => {
    if (!stretch.images) return [];
    const h = hidden[stretch.id] || [];
    return stretch.images.filter(img => !h.includes(img));
  }, [hidden]);

  const handleDelete = (stretchId, imgUrl) => {
    if (locked[stretchId]?.includes(imgUrl)) return;
    const upd = { ...hidden, [stretchId]: [...(hidden[stretchId] || []), imgUrl] };
    saveHidden(upd); setHidden(upd);
  };

  const handleRestore = (stretchId) => {
    const upd = { ...hidden }; delete upd[stretchId];
    saveHidden(upd); setHidden(upd);
  };

  const handleToggleLock = (stretchId, imgUrl) => {
    const upd = { ...locked };
    if (!upd[stretchId]) upd[stretchId] = [];
    if (upd[stretchId].includes(imgUrl)) {
      upd[stretchId] = upd[stretchId].filter(u => u !== imgUrl);
    } else {
      upd[stretchId].push(imgUrl);
    }
    saveLocked(upd); setLocked(upd);
  };

  const handleReplace = (stretchId, oldUrl, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      const newUrl = ev.target.result;
      // Store replacement in localStorage
      const replacements = JSON.parse(localStorage.getItem('stretch_replacements') || '{}');
      if (!replacements[stretchId]) replacements[stretchId] = {};
      replacements[stretchId][oldUrl] = newUrl;
      localStorage.setItem('stretch_replacements', JSON.stringify(replacements));
      // Force re-render
      setReplaceTarget(null);
      setImgIndex({ ...imgIndex });
    };
    reader.readAsDataURL(file);
  };

  const getDisplayUrl = (stretchId, originalUrl) => {
    try {
      const replacements = JSON.parse(localStorage.getItem('stretch_replacements') || '{}');
      return replacements[stretchId]?.[originalUrl] || originalUrl;
    } catch { return originalUrl; }
  };

  const zone = selectedZone ? BODY_ZONES.find(z => z.id === selectedZone) : null;
  const zoneColor = selectedZone ? ZONE_COLORS[selectedZone] : '#f59e0b';

  const [keepAwake, toggleKeepAwake] = useKeepAwake('stretching', isOpen);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950" data-testid="stretching-panel">
      <div className="absolute inset-0 bg-black/95" onClick={onClose} />
      <div className="relative z-10 w-full h-full max-w-lg mx-auto flex flex-col" style={{ maxHeight: '100dvh' }}>

        {/* BODY MAP */}
        {!selectedZone && (
          <div className="flex flex-col h-full px-4 pt-12 pb-24 animate-fadeIn">
            <div className="text-center mb-4">
              <button onClick={onClose} className="absolute top-10 right-4 text-zinc-500 hover:text-white p-2 transition-colors" data-testid="stretching-close">
                <X size={22} />
              </button>
              <h2 className="text-2xl font-semibold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                <span className="bg-gradient-to-r from-cyan-400 to-blue-400 bg-clip-text text-transparent">Étirements</span>
              </h2>
              <p className="text-zinc-500 text-sm mb-3">Touchez une zone du corps</p>
              <button onClick={() => setShowSessionAudio(true)}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-medium bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/25 transition"
                data-testid="stretching-session-settings">
                <SlidersHorizontal className="w-3.5 h-3.5" /> Paramétrer votre séance
              </button>
              <div className="mt-2 flex justify-center">
                <KeepAwakeToggle keepAwake={keepAwake} onToggle={toggleKeepAwake} testId="keep-awake-stretching"
                  className="px-4 py-2 rounded-full text-xs font-medium bg-cyan-500/15 border border-cyan-400/30 text-cyan-300 hover:bg-cyan-500/25" />
              </div>
            </div>
            <div className="flex-1 min-h-0 flex justify-center pt-3">
              <BodySVG onZoneClick={setSelectedZone} activeZone={null} />
            </div>
            {/* Quick access buttons */}
            <div className="flex flex-wrap gap-2 justify-center pt-3 pb-2">
              {BODY_ZONES.map(z => (
                <button key={z.id} onClick={() => setSelectedZone(z.id)}
                  className="text-xs px-3 py-1.5 rounded-full border transition-all hover:scale-105 active:scale-95"
                  style={{ borderColor: `${ZONE_COLORS[z.id]}40`, color: ZONE_COLORS[z.id], background: `${ZONE_COLORS[z.id]}10` }}
                  data-testid={`quick-${z.id}`}>
                  {z.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* STRETCH LIST */}
        {selectedZone && zone && (
          <div className="flex flex-col h-full px-4 pt-6 pb-24 animate-fadeIn" data-testid="stretch-list">
            <div className="flex items-center justify-between mb-5">
              <button onClick={() => { setSelectedZone(null); setExpandedStretch(null); }}
                className="flex items-center gap-2 text-white/80 hover:text-white transition-colors"
                data-testid="stretch-back">
                <ArrowLeft size={20} />
                <span className="text-sm">Retour</span>
              </button>
              <h3 className="font-semibold text-base" style={{ color: zoneColor }}>{zone.label}</h3>
            </div>

            {/* Mini body indicator */}
            <div className="mb-4 flex justify-center">
              <div className="w-20">
                <BodySVG onZoneClick={() => {}} activeZone={selectedZone} />
              </div>
            </div>

            <p className="text-white/40 text-xs mb-4 text-center">{zone.stretches.length} étirements disponibles</p>

            <div className="flex-1 overflow-y-auto space-y-3">
              {zone.stretches.map((stretch, i) => (
                <div key={stretch.name || i} className="rounded-2xl border overflow-hidden transition-all"
                  style={{ borderColor: expandedStretch === i ? `${zoneColor}50` : 'rgba(255,255,255,0.1)', background: expandedStretch === i ? `${zoneColor}08` : 'rgba(255,255,255,0.03)' }}>
                  <button onClick={() => setExpandedStretch(expandedStretch === i ? null : i)}
                    className="w-full flex items-center justify-between p-4"
                    data-testid={`stretch-${i}`}>
                    <div className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold"
                        style={{ background: `${zoneColor}20`, color: zoneColor }}>
                        {i + 1}
                      </span>
                      <div className="text-left">
                        <p className="text-white font-medium text-sm">{stretch.name}</p>
                        <p className="text-white/40 text-xs flex items-center gap-1 mt-0.5">
                          <Clock size={10} /> {stretch.duration}
                        </p>
                      </div>
                    </div>
                    <ChevronRight size={18} className={`text-white/30 transition-transform ${expandedStretch === i ? 'rotate-90' : ''}`} />
                  </button>

                  {expandedStretch === i && (
                    <div className="px-4 pb-4 space-y-3 animate-fadeIn">
                      <div className="w-full h-px" style={{ background: `${zoneColor}20` }} />

                      {/* Image Gallery */}
                      {(() => {
                        const visibleImgs = getVisibleImages(stretch);
                        const idx = imgIndex[stretch.id] || 0;
                        const safeIdx = Math.min(idx, visibleImgs.length - 1);
                        const currentImg = visibleImgs[safeIdx];
                        const hiddenCount = (hidden[stretch.id] || []).length;
                        const customVideo = poseMedia[stretch.id];
                        const uploadingThis = poseUploads?.[stretch.id];

                        // Custom video uploaded for this stretch -> replaces gallery
                        if (customVideo) {
                          return (
                            <div>
                              <div className="relative rounded-xl overflow-hidden mb-2 bg-black" style={{ height: '180px' }}>
                                <video
                                  src={customVideo.url}
                                  autoPlay
                                  loop
                                  muted
                                  playsInline
                                  controls
                                  className="w-full h-full object-cover"
                                  data-testid={`stretch-video-${stretch.id}`}
                                />
                                <button
                                  onClick={() => setFsVideo({ url: customVideo.url, name: stretch.name })}
                                  className="absolute top-2 left-2 z-10 p-2 rounded-full bg-black/60 hover:bg-black text-white border border-white/20"
                                  aria-label="Agrandir la vidéo"
                                  data-testid={`stretch-video-fullscreen-${stretch.id}`}>
                                  <Maximize2 size={14} />
                                </button>
                                {isAdmin && (
                                  <PoseMediaControls
                                    poseId={stretch.id}
                                    hasMedia={true}
                                    locked={!!customVideo.locked}
                                    onUpload={(file) => uploadMedia(stretch.id, file)}
                                    onDelete={() => removeMedia(stretch.id)}
                                    onToggleLock={() => toggleMediaLock(stretch.id)}
                                  />
                                )}
                                {uploadingThis && (
                                  <UploadProgressOverlay info={uploadingThis} poseId={stretch.id} />
                                )}
                              </div>
                            </div>
                          );
                        }

                        if (visibleImgs.length === 0 && hiddenCount > 0 && isAdmin) {
                          return (
                            <button onClick={() => handleRestore(stretch.id)}
                              className="w-full py-2 rounded-xl bg-amber-500/15 border border-amber-500/30 text-amber-400 text-xs">
                              Restaurer {hiddenCount} image{hiddenCount > 1 ? 's' : ''}
                            </button>
                          );
                        }
                        if (!currentImg) return null;
                        return (
                          <div>
                            {/* Main image */}
                            <div className="relative rounded-xl overflow-hidden mb-2" style={{ height: '180px' }}>
                              <img src={getDisplayUrl(stretch.id, currentImg)} alt={stretch.name}
                                className="w-full h-full object-cover" loading="lazy" />
                              {/* Nav arrows */}
                              {visibleImgs.length > 1 && (
                                <>
                                  <button onClick={() => setImgIndex({ ...imgIndex, [stretch.id]: (safeIdx - 1 + visibleImgs.length) % visibleImgs.length })}
                                    className="absolute left-1.5 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-7 h-7 flex items-center justify-center z-10">
                                    <ChevronLeft size={14} />
                                  </button>
                                  <button onClick={() => setImgIndex({ ...imgIndex, [stretch.id]: (safeIdx + 1) % visibleImgs.length })}
                                    className="absolute right-1.5 top-1/2 -translate-y-1/2 bg-black/50 text-white rounded-full w-7 h-7 flex items-center justify-center z-10">
                                    <ChevronRight size={14} />
                                  </button>
                                  <div className="absolute top-2 right-2 bg-black/50 text-white/80 text-[10px] px-2 py-0.5 rounded-full z-10 flex items-center gap-1">
                                    <Images size={10} /> {safeIdx + 1}/{visibleImgs.length}
                                  </div>
                                </>
                              )}
                              {/* Admin: delete button on image */}
                              {isAdmin && visibleImgs.length > 0 && !locked[stretch.id]?.includes(currentImg) && (
                                <button onClick={() => { handleDelete(stretch.id, currentImg); if (safeIdx > 0) setImgIndex({ ...imgIndex, [stretch.id]: safeIdx - 1 }); }}
                                  className="absolute bottom-2 right-2 bg-red-600/80 text-white rounded-full px-2 py-1 flex items-center gap-1 text-[10px] z-10 hover:bg-red-500">
                                  <Trash2 size={10} /> Supprimer
                                </button>
                              )}
                              {/* Admin: upload custom video to replace this stretch's gallery */}
                              {isAdmin && (
                                <PoseMediaControls
                                  poseId={stretch.id}
                                  hasMedia={false}
                                  locked={false}
                                  onUpload={(file) => uploadMedia(stretch.id, file)}
                                  onDelete={() => removeMedia(stretch.id)}
                                  onToggleLock={() => toggleMediaLock(stretch.id)}
                                />
                              )}
                              {uploadingThis && (
                                <UploadProgressOverlay info={uploadingThis} poseId={stretch.id} />
                              )}
                            </div>

                            {/* Thumbnails */}
                            {visibleImgs.length > 1 && (
                              <div className="flex gap-1.5 mb-2 overflow-x-auto">
                                {visibleImgs.map((img, ti) => (
                                  <div key={`${stretch.id}-thumb-${ti}`} className="relative flex-shrink-0">
                                    <button onClick={() => setImgIndex({ ...imgIndex, [stretch.id]: ti })}
                                      className={`w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${ti === safeIdx ? 'scale-105' : 'opacity-60 hover:opacity-90'}`}
                                      style={{ borderColor: ti === safeIdx ? zoneColor : 'rgba(255,255,255,0.1)' }}>
                                      <img src={getDisplayUrl(stretch.id, img)} alt="" className="w-full h-full object-cover" loading="lazy" />
                                    </button>
                                    {/* Admin: tiny delete on thumb */}
                                    {isAdmin && !locked[stretch.id]?.includes(img) && (
                                      <button onClick={() => { handleDelete(stretch.id, img); }}
                                        className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white rounded-full flex items-center justify-center z-10">
                                        <X size={8} />
                                      </button>
                                    )}
                                    {/* Admin: lock indicator */}
                                    {isAdmin && locked[stretch.id]?.includes(img) && (
                                      <div className="absolute -top-1 -right-1 w-4 h-4 bg-amber-500 rounded-full flex items-center justify-center z-10">
                                        <Lock size={8} className="text-black" />
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            )}

                            {/* Admin toolbar */}
                            {isAdmin && (
                              <div className="flex gap-1.5 flex-wrap">
                                <button onClick={() => handleToggleLock(stretch.id, currentImg)}
                                  className={`flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] transition-all ${locked[stretch.id]?.includes(currentImg) ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30' : 'bg-white/5 text-white/50 border border-white/10'}`}>
                                  {locked[stretch.id]?.includes(currentImg) ? <><Lock size={10} /> Verrouillée</> : <><Unlock size={10} /> Verrouiller</>}
                                </button>
                                <label className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] bg-white/5 text-white/50 border border-white/10 cursor-pointer hover:text-amber-400">
                                  <Upload size={10} /> Remplacer
                                  <input type="file" accept="image/*" className="hidden"
                                    onChange={(e) => handleReplace(stretch.id, currentImg, e)} />
                                </label>
                                {hiddenCount > 0 && (
                                  <button onClick={() => handleRestore(stretch.id)}
                                    className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] bg-emerald-500/15 text-emerald-400 border border-emerald-500/25">
                                    Restaurer ({hiddenCount})
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Steps */}
                      {stretch.steps.map((step, j) => (
                        <div key={step} className="flex items-start gap-3">
                          <span className="w-5 h-5 rounded-full text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5"
                            style={{ background: `${zoneColor}15`, color: zoneColor }}>
                            {j + 1}
                          </span>
                          <p className="text-white/70 text-sm leading-relaxed">{step}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.35s ease forwards; }
      `}</style>

      <SessionAudioPanel
        isOpen={showSessionAudio}
        onClose={() => setShowSessionAudio(false)}
        section="stretching"
        isAdmin={isAdmin}
        showVoices={false}
      />

      {fsVideo && <StretchFullscreenPlayer video={fsVideo} onClose={() => setFsVideo(null)} />}
    </div>
  );
};

function StretchFullscreenPlayer({ video, onClose }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
    else if (el?.webkitRequestFullscreen) el.webkitRequestFullscreen();
    const handleExit = () => {
      if (!document.fullscreenElement && !document.webkitFullscreenElement) onClose();
    };
    document.addEventListener('fullscreenchange', handleExit);
    document.addEventListener('webkitfullscreenchange', handleExit);
    return () => {
      document.removeEventListener('fullscreenchange', handleExit);
      document.removeEventListener('webkitfullscreenchange', handleExit);
    };
  }, [onClose]);

  const closeAndExit = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.webkitFullscreenElement) document.webkitExitFullscreen();
    } catch { /* noop */ }
    onClose();
  };

  return (
    <div ref={containerRef} className="fixed inset-0 z-[400] bg-black flex items-center justify-center"
      data-testid="stretch-fullscreen-player">
      <video src={video.url} controls autoPlay loop playsInline className="w-full h-full object-contain" />
      <button onClick={closeAndExit}
        className="absolute top-3 left-3 z-10 flex items-center gap-2 px-4 py-2.5 rounded-full bg-black/70 hover:bg-black text-white text-sm font-medium border border-white/25"
        data-testid="stretch-fullscreen-back"
        aria-label="Retour">
        <ArrowLeft className="w-4 h-4" /> Retour
      </button>
    </div>
  );
}

export default StretchingPanel;
